import React, { useEffect, useState } from "react";
import styles from "./CitySelectionModalDesktop.module.scss";
import ModalManager from "@components/molecules/ModalManager";
import { FALLBACK_CITIES } from "@constants/cities";
import CityListItem from "@components/molecules/cityListItem";
import CityGridItem from "@components/molecules/CityGridItem";
import CitySelectionDesktopHeader from "@components/molecules/CitySelectionDesktopHeader";
import Image from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";
import { getCities } from "@services/cityService";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { City, CitySelectionModalProps } from "../City.types";
import { usePathname } from "next/navigation";
import { useCity } from "@context/CityContext";
import { getPageNameFromUrl } from "@utils/pageNameUtils";
import { normalizeCityName } from "@utils/urlNormalization";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { DARK_THEME_APPBAR_CONFIG, COLLECTION_HEADER_APPBAR_CONFIG } from "@utils/appbarConfig";
import Cookies from 'js-cookie';

export default function CitySelectionModalDesktop({
  selectedCity,
  isModalOpen,
  setIsModalOpen,
  handleCitySelection,
}: CitySelectionModalProps) {
  const [cities, setCities] = useState<City[]>([]);
  const { setCity: setContextCity } = useCity();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [search, setSearch] = useState("");
  const { showLoader, hideLoader } = useGlobalLoader();
  const pathname = usePathname();
  
  const isBuyCarPage = pathname === '/cars' || pathname === '/cars/';
  const isWebView = Boolean(Cookies.get('webview'));
  const isMmvOrMmvcPage = /^\/cars\/[^/]+\/?$/.test(pathname || '');
  
  const closeModal = () => {
    setIsModalOpen(false);
    
    if (isBuyCarPage && isWebView) {
      updateAppBar(COLLECTION_HEADER_APPBAR_CONFIG);
    }
  };

  const handleCityChange = (newCity: any) => {
    setContextCity(newCity);
    closeModal();
    showLoader();
    if (handleCitySelection) {
      handleCitySelection(newCity);
    } else {
      if (pathname?.includes('price-in-')) {
        const normalizedCityName = normalizeCityName(newCity.name || '');
        const newPath = pathname.replace(/price-in-[^/]+/, `price-in-${normalizedCityName}`);
        window.location.href = newPath;
      } else if (isMmvOrMmvcPage) {
        const url = new URL(window.location.href);
        url.searchParams.set(
          'city',
          newCity.city_slug || normalizeCityName(newCity.name || '')
        );
        window.location.href = url.toString();
      } else {
        window.location.reload();
      }
    }
  };

  useEffect(() => {
    async function fetchCities() {
      setLoading(true);
      const activeCities = await getCities();
      setCities(activeCities.length ? activeCities : FALLBACK_CITIES);
      setLoading(false);

      if (isModalOpen) {
        if (isBuyCarPage && isWebView) {
          updateAppBar(DARK_THEME_APPBAR_CONFIG);
        }

        const currentSelectedCity = getSelectedCityClient();
        await trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
          pageType: getPageNameFromUrl(pathname),
          additionalProps: {
            view_type: "view_page",
            view_name: "view_list_of_cities",
            city_name: currentSelectedCity?.name || "",
          },
        });
      }
    }
    fetchCities();
  }, [isModalOpen, isBuyCarPage, isWebView]);

  const filteredCities = search
    ? cities.filter((c) => c.name?.toLowerCase().includes(search.toLowerCase()))
    : cities;

  const popularCities = cities.filter(
    (city) =>
      city?.city_config?.is_live &&
      city?.city_config?.logo_image &&
      city?.city_config?.sorting_rank > 0
  );

  const handleSelect = (city: City) => {
    handleCityChange(city);
    closeModal();
    setSearch("");
  };

  return (
    <ModalManager
      isOpen={isModalOpen}
      onClose={closeModal}
      preventScroll={false}
      customClass={styles.modal}
      modalBodyClass={styles.modalBody}
      showClose={false}
    >
      <div className={styles.modalContainer}>
        <CitySelectionDesktopHeader
          search={search}
          setSearch={setSearch}
          closeModal={closeModal}
          handleSelect={handleSelect}
          cities={cities}
        />
        <div className={styles.scrollableContent}>
          {loading ? (
            <div className={styles.loading}>Loading cities...</div>
          ) : error ? (
            <div className={styles.error}>Failed to load cities.</div>
          ) : search && filteredCities.length === 0 ? (
            <div className={styles.emptyState}>
              <Image
                src={`${assetLoader()}/icons/CitySelection/NoVehicle.svg`}
                width={104}
                height={104}
                alt="location"
                className={styles.NoVehicleIcon}
              />
              <div className={styles.emptyHeading}>Location not found</div>
              <div className={styles.emptySubHeading}>
                We couldn’t find that location. Please check the spelling or try
                a nearby city.
              </div>
            </div>
          ) : (
            <>
              {!search && popularCities?.length > 0 && (
                <div className={styles.sectionContainer}>
                  <h3 className={styles.sectionTitle}>Popular Cities</h3>
                  <div className={styles.cityGrid}>
                    {popularCities.map((city) => (
                      <CityGridItem
                        key={city.id}
                        city={city}
                        selectedCity={selectedCity}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className={styles.sectionContainer}>
                <h3 className={styles.sectionTitle}>All Cities</h3>
                <div className={styles.cityList}>
                  {filteredCities?.map((city) => (
                    <CityListItem
                      key={city.id}
                      city={city}
                      selectedCity={selectedCity}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </ModalManager>
  );
}
